import React, { useMemo } from 'react'
import { Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import isNil from 'lodash/isNil'
import {
  CustomerOption,
  SortDirection,
  UserListRow,
  type UserListStats,
  type UserStatusBucket,
} from '../../api/dto/user-invitation.dto'
import {
  AccessFilterPanel,
  CompanyFilterPanel,
  StatusFilterPanel,
} from './TableFilterPanels'
import {
  getRoleAccessConfig,
  isEmployeeRbacRoleName,
  isHrAdminRbacRoleName,
} from './userDisplay'
import {
  abbreviatedTitle,
  renderAccessCell,
  renderCompanyCell,
  renderDateTime,
  renderEmailCell,
  renderFilterIcon,
  renderNameCell,
  renderStatusCell,
} from './usersTableCells'

interface UseUsersColumnsParams {
  sortBy: string | null
  sortDir: SortDirection | null
  customerOptions: CustomerOption[]
  /** True while the company options are still loading. */
  areCompaniesLoading: boolean
  clientPublicKeys: string[]
  hasHrAdminAccess: boolean | null
  hasEmployeeAccess: boolean | null
  statusFilters: UserStatusBucket[]
  stats: UserListStats | null
  onCompaniesChange: (values: string[]) => void
  onHrAdminAccessChange: (value: boolean | null) => void
  onEmployeeAccessChange: (value: boolean | null) => void
  onStatusFilterChange: (value: UserStatusBucket[]) => void
}

const toAntdOrder = (
  dir: SortDirection | null
): 'ascend' | 'descend' | undefined =>
  dir === 'asc' ? 'ascend' : dir === 'desc' ? 'descend' : undefined

/**
 * The user list's columns. Every row is read-only here: granting and removing
 * act on a selection through the bulk bar, and the row itself opens history.
 */
export const useUsersColumns = ({
  sortBy,
  sortDir,
  customerOptions,
  areCompaniesLoading,
  clientPublicKeys,
  hasHrAdminAccess,
  hasEmployeeAccess,
  statusFilters,
  stats,
  onCompaniesChange,
  onHrAdminAccessChange,
  onEmployeeAccessChange,
  onStatusFilterChange,
}: UseUsersColumnsParams): ColumnsType<UserListRow> =>
  useMemo(
    () => [
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        // Frozen so the address stays on screen while the access and date
        // columns scroll past it. A frozen column has to carry its own width,
        // so this is counted into scroll.x rather than absorbing the leftover.
        fixed: 'left',
        width: 320,
        ellipsis: true,
        sorter: true,
        sortOrder: sortBy === 'email' ? toAntdOrder(sortDir) : null,
        render: renderEmailCell,
      },
      {
        title: 'Name',
        dataIndex: 'fullName',
        key: 'fullName',
        width: 200,
        ellipsis: true,
        render: renderNameCell,
      },
      {
        title: 'Billing Company',
        key: 'companies',
        width: 240,
        // By the name shown below, which is the alphabetically first of a row's
        // companies. Rows with none sort last, ascending or descending.
        sorter: true,
        sortOrder: sortBy === 'companies' ? toAntdOrder(sortDir) : null,
        filterIcon: () => renderFilterIcon(clientPublicKeys.length > 0),
        filterDropdown: () => (
          <CompanyFilterPanel
            options={customerOptions}
            isLoading={areCompaniesLoading}
            selectedKeys={clientPublicKeys}
            onChange={onCompaniesChange}
          />
        ),
        render: (_: unknown, record: UserListRow) => renderCompanyCell(record),
      },
      {
        title: 'HR Admin',
        key: 'hrAdmin',
        width: 110,
        filterIcon: () => renderFilterIcon(!isNil(hasHrAdminAccess)),
        filterDropdown: () => (
          <AccessFilterPanel
            value={hasHrAdminAccess}
            onChange={onHrAdminAccessChange}
          />
        ),
        render: (_: unknown, record: UserListRow) =>
          renderAccessCell(getRoleAccessConfig(record, isHrAdminRbacRoleName)),
      },
      {
        title: (
          <Tooltip title="Employee Experience">
            <span>EE</span>
          </Tooltip>
        ),
        key: 'ee',
        width: 80,
        filterIcon: () => renderFilterIcon(!isNil(hasEmployeeAccess)),
        filterDropdown: () => (
          <AccessFilterPanel
            value={hasEmployeeAccess}
            onChange={onEmployeeAccessChange}
          />
        ),
        render: (_: unknown, record: UserListRow) =>
          renderAccessCell(getRoleAccessConfig(record, isEmployeeRbacRoleName)),
      },
      {
        // Where this row stands in the access this screen grants, from never
        // invited through to holding the role. It is the whole lifecycle in one
        // column, so there is no separate status column to read alongside it.
        title: 'Finance Manager',
        key: 'fundingInvoice',
        width: 180,
        filterIcon: () => renderFilterIcon(statusFilters.length > 0),
        filterDropdown: () => (
          <StatusFilterPanel
            value={statusFilters}
            stats={stats}
            onChange={onStatusFilterChange}
          />
        ),
        render: (_: unknown, record: UserListRow) => renderStatusCell(record),
      },
      {
        title: 'Invite date',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 130,
        sorter: true,
        sortOrder: sortBy === 'createdAt' ? toAntdOrder(sortDir) : null,
        render: renderDateTime,
      },
      {
        title: 'Registered date',
        dataIndex: 'activatedAt',
        key: 'activatedAt',
        width: 130,
        sorter: true,
        sortOrder: sortBy === 'activatedAt' ? toAntdOrder(sortDir) : null,
        render: renderDateTime,
      },
      {
        title: 'Last access',
        dataIndex: 'lastAccessAt',
        key: 'lastAccessAt',
        width: 130,
        sorter: true,
        sortOrder: sortBy === 'lastAccessAt' ? toAntdOrder(sortDir) : null,
        // Stamped on sign-in and on each silent token refresh, so it reflects
        // real usage rather than the last time a password was typed.
        render: renderDateTime,
      },
      {
        title: abbreviatedTitle('Invoice access', 'Last invoice access'),
        dataIndex: 'lastInvoiceAccessAt',
        key: 'lastInvoiceAccessAt',
        width: 150,
        sorter: true,
        sortOrder:
          sortBy === 'lastInvoiceAccessAt' ? toAntdOrder(sortDir) : null,
        // Sparse by design: populated from analytics events going forward, with
        // no backfill, so an empty cell means "not since release", not "never".
        render: renderDateTime,
      },
      {
        title: abbreviatedTitle('Download', 'Last invoice download'),
        dataIndex: 'lastInvoiceDownloadAt',
        key: 'lastInvoiceDownloadAt',
        width: 130,
        sorter: true,
        sortOrder:
          sortBy === 'lastInvoiceDownloadAt' ? toAntdOrder(sortDir) : null,
        render: renderDateTime,
      },
    ],
    [
      sortBy,
      sortDir,
      clientPublicKeys,
      customerOptions,
      areCompaniesLoading,
      onCompaniesChange,
      hasHrAdminAccess,
      hasEmployeeAccess,
      onHrAdminAccessChange,
      onEmployeeAccessChange,
      statusFilters,
      stats,
      onStatusFilterChange,
    ]
  )
